import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const ST = { pending: "In attesa di verifica", in_progress: "Attivazione in corso con Meta", active: "Attivo", rejected: "Da correggere" };
const STEPS = [
  "Inserisci il numero che vuoi usare con gli ospiti (cellulare o fisso della struttura).",
  "Se quel numero usa già l'app WhatsApp o WhatsApp Business, va prima eliminato l'account dall'app (Impostazioni → Account → Elimina account). Le chat non si recuperano: salvale prima.",
  "Noi registriamo il numero e Meta verifica il nome visualizzato: riceverai un codice via SMS o chiamata da comunicarci.",
  "In 1–3 settimane il numero è attivo: Otelya risponde agli ospiti in tutte le lingue, 24 ore su 24.",
];

function AdminList() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/whatsapp/senders").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const set = async (id, status) => {
    const note = status === "rejected" ? window.prompt("Cosa deve correggere la struttura?") || "" : "";
    try { await api.put(`/admin/whatsapp/senders/${id}`, { status, note }); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5 space-y-3" data-testid="wa-admin-list">
      <div className="font-serif text-lg text-ink">Richieste delle strutture</div>
      {!rows.length && <div className="text-sm text-stone">Nessuna richiesta.</div>}
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-line py-2 text-sm" data-testid={`wa-req-${r.id}`}>
          <b>{r.business_name}</b><span>{r.number}</span><span className="text-stone">"{r.display_name}"</span>
          <span className="text-xs uppercase tracking-widest text-stone">{ST[r.status]}</span>
          <div className="ml-auto flex gap-2">
            {["in_progress", "active", "rejected"].map((s) => <Button key={s} size="sm" variant="outline" className="rounded-full h-8" onClick={() => set(r.id, s)} data-testid={`wa-set-${s}-${r.id}`}>{ST[s]}</Button>)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WhatsAppConnect() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [f, setF] = useState({ number: "", display_name: user?.property_name || "", business_name: user?.property_name || "", website: "", already_on_whatsapp: false });
  useEffect(() => { api.get("/whatsapp/sender").then((r) => setS(r.data)); }, []);
  const send = async () => {
    try { const r = await api.post("/whatsapp/sender", f); setS(r.data); toast.success("Richiesta inviata"); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  if (!s) return null;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="wa-connect">
      <ol className="space-y-2">{STEPS.map((t, i) => <li key={i} className="flex items-baseline gap-3 text-sm text-ink"><span className="font-serif text-xl text-gold w-6">{i + 1}</span><span>{t}</span></li>)}</ol>
      {s.status && (
        <div className="rounded-2xl border border-line bg-canvas p-5 flex items-center gap-3" data-testid="wa-status">
          <WhatsappLogo size={28} weight="fill" className="text-olive" />
          <div><div className="text-ink font-medium">{s.number} · {s.display_name}</div><div className="text-sm text-stone">{ST[s.status]}{s.admin_note ? ` — ${s.admin_note}` : ""}</div></div>
        </div>
      )}
      {(!s.status || s.status === "rejected") && (
        <div className="rounded-2xl border border-line bg-canvas p-5 space-y-3" data-testid="wa-form">
          <Input placeholder="Numero WhatsApp (+39…)" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="wa-number" />
          <Input placeholder="Nome visualizzato dagli ospiti (es. Hotel Bellavista)" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="wa-display-name" />
          <Input placeholder="Ragione sociale" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="wa-business-name" />
          <Input placeholder="Sito web (facoltativo)" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="wa-website" />
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.already_on_whatsapp} onCheckedChange={(v) => setF({ ...f, already_on_whatsapp: v })} data-testid="wa-already" />Il numero usa già WhatsApp sul telefono</label>
          <Button className="rounded-full bg-ink text-canvas" disabled={!f.number || !f.display_name || !f.business_name} onClick={send} data-testid="wa-submit">Collega il mio WhatsApp</Button>
        </div>
      )}
      {user?.email === "alfiomoretti@icloud.com" && <AdminList />}
    </div>
  );
}
